"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { Key, Tab, TabList, TabPanel, Tabs } from "react-aria-components";
import { clsx } from "clsx";

import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";

import { Center, CenterFilled } from "./Center";
import { InnerDefault, InnerFilled } from "./Inner";
import { HorizontalLine, VerticalLine } from "./Lines";
import { Outer, OuterFilled } from "./Outer";

type GraphicType = "center" | "inner" | "outer";

export interface Slide {
  title: string;
  description: string;
}

export interface TechnologyLayersGraphicUIProps {
  slides: Slide[];
}

const SLIDE_TO_GRAPHIC: Record<string, GraphicType> = {
  "1": "inner",
  "2": "center",
  "3": "outer",
};

const GRAPHIC_TO_SLIDE: Record<GraphicType, string> = {
  inner: "1",
  center: "2",
  outer: "3",
};

const Graphic = ({
  onGraphicChange,
  selectedGraphic,
}: {
  onGraphicChange: (graphic: GraphicType) => void;
  selectedGraphic: GraphicType;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to determine which layer is actually being hovered
  const determineHoveredLayer = useCallback((event: React.MouseEvent) => {
    if (!containerRef.current) return null;

    // Get all elements at the mouse position, ordered from top to bottom
    const elementsAtPoint = document.elementsFromPoint(event.clientX, event.clientY);

    const containerElements = elementsAtPoint.filter(element => containerRef.current?.contains(element));
    const elementIds = containerElements
      .filter(element => element.tagName === "svg")
      .map(el => el.id)
      .filter(id => id !== "horizontal-line" && id !== "vertical-line");
    const elementCount = elementIds.length;
    const elementLookup = elementIds.reduce(
      (acc, id) => {
        acc[id] = id;
        return acc;
      },
      {} as Record<string, string>,
    );

    if (
      (elementCount === 3 && !elementIds.some(id => id.includes("filled"))) || // nothing is filled, three layers overlap
      (elementCount === 4 && elementLookup["inner-filled"]) || // inner is currently filled, three default + 1 filled layer
      (elementCount === 4 && elementLookup["center-filled"]) // going from center to inner
    ) {
      return "inner";
    }
    if (
      (elementCount === 2 && !elementIds.some(id => id.includes("filled"))) || // nothing is filled, two layers overlap
      (elementCount === 3 && elementLookup["center-filled"]) || // center is currently filled, two default + 1 filled layer
      (elementCount === 3 && elementLookup["outer-filled"]) // going from outer to center
    ) {
      return "center";
    }
    if (
      elementCount === 1 || // nothing is filled, one layer overlap
      (elementCount === 2 && elementLookup["outer-filled"]) // outer is currently filled, one default + 1 filled layer
    ) {
      return "outer";
    }

    return null;
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      const layer = determineHoveredLayer(event);
      if (layer) {
        onGraphicChange(layer);
      }
    },
    [determineHoveredLayer, onGraphicChange],
  );

  return (
    <div
      className='tw-relative tw-mx-auto tw-max-w-[782px] tw-overflow-hidden'
      onMouseMove={handleMouseMove}
      ref={containerRef}
    >
      <div className='tw-relative tw-mx-auto tw-h-[320px] tw-max-w-[677px]'>
        {selectedGraphic === "inner" && (
          <InnerFilled className='tw-absolute tw-inset-0 tw-left-1/2 tw-top-1/2 tw-z-[3] tw-h-full tw-w-[39%] -tw-translate-x-1/2 -tw-translate-y-1/2' />
        )}
        <InnerDefault className='tw-absolute tw-inset-0 tw-left-1/2 tw-top-1/2 tw-z-[3] tw-h-full tw-w-[39%] -tw-translate-x-1/2 -tw-translate-y-1/2' />
        {selectedGraphic === "center" && (
          <CenterFilled className='tw-absolute tw-inset-0 tw-left-1/2 tw-top-1/2 tw-z-[3] tw-h-full tw-w-[68%] -tw-translate-x-1/2 -tw-translate-y-1/2' />
        )}
        <Center className='tw-absolute tw-inset-0 tw-left-1/2 tw-top-1/2 tw-z-[2] tw-h-full tw-w-[68%] -tw-translate-x-1/2 -tw-translate-y-1/2' />
        {selectedGraphic === "outer" && (
          <OuterFilled className='tw-absolute tw-inset-0 tw-left-1/2 tw-top-1/2 tw-z-[3] tw-h-full tw-w-[97%] -tw-translate-x-1/2 -tw-translate-y-1/2' />
        )}
        <Outer className='tw-absolute tw-inset-0 tw-left-1/2 tw-top-1/2 tw-z-[1] tw-h-full tw-w-[97%] -tw-translate-x-1/2 -tw-translate-y-1/2' />
      </div>
      <HorizontalLine className='tw-absolute tw-inset-0 tw-left-1/2 tw-top-1/2 tw-w-full -tw-translate-x-1/2 -tw-translate-y-1/2' />
      <VerticalLine className='tw-absolute tw-left-1/2 tw-top-1/2 tw-z-[5] tw-w-[140px] tw-origin-left -tw-translate-y-1/2 tw-rotate-90 lg:tw-w-[251px]' />
      {/* Line end indicators */}
      <div className='tw-absolute tw-left-0 tw-top-1/2 tw-size-2 -tw-translate-y-1/2 tw-rounded-px tw-bg-gray-300 tw-z-10 tw-rounded-sm' />
      <div className='tw-absolute tw-right-0 tw-top-1/2 tw-size-2 -tw-translate-y-1/2 tw-rounded-px tw-bg-gray-300 tw-z-10 tw-rounded-sm' />
      <div className='tw-absolute tw-bottom-0 tw-left-1/2 tw-size-2 -tw-translate-x-1/2 tw-rounded-px tw-bg-black tw-z-10 tw-rounded-sm' />
    </div>
  );
};

export function TechnologyLayersGraphicUI({ slides }: TechnologyLayersGraphicUIProps) {
  const [selectedSlide, setSelectedSlide] = useState<Key>("1");

  const selectedGraphic = useMemo(() => {
    return SLIDE_TO_GRAPHIC[selectedSlide] ?? "inner";
  }, [selectedSlide]);

  const handleGraphicChange = useCallback((graphic: GraphicType) => {
    setSelectedSlide(GRAPHIC_TO_SLIDE[graphic]);
  }, []);

  const handleSelectionChange = useCallback((key: Key) => {
    setSelectedSlide(key);
  }, []);

  const tabItems = useMemo(
    () =>
      slides.map((_, index) => ({
        id: String(index + 1),
        title: String(index + 1),
      })),
    [slides],
  );

  return (
    <div className='tw-relative tw-overflow-hidden tw-rounded-2xl tw-bg-cream tw-px-6 tw-py-10 md:tw-px-10 md:tw-py-15'>
      <HairlineCardBorder />
      <Graphic onGraphicChange={handleGraphicChange} selectedGraphic={selectedGraphic} />
      <Tabs selectedKey={selectedSlide} onSelectionChange={handleSelectionChange}>
        <div className='tw-relative tw-mt-8'>
          {slides.map((slide, index) => (
            <TabPanel
              key={String(index + 1)}
              id={String(index + 1)}
              className={clsx(
                "tw-mx-auto tw-flex tw-min-h-[140px] tw-max-w-2xl tw-flex-col tw-items-center tw-text-center lg:tw-min-h-[100px]",
                selectedSlide !== String(index + 1) && "tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-0",
              )}
            >
              <h3 className='tw-type-lg tw-mb-4 tw-min-h-14 tw-font-medium md:tw-min-h-0'>{slide.title}</h3>
              <p className='tw-type-sm tw-min-h-40 tw-max-w-lg tw-text-stone-500 md:tw-min-h-0'>{slide.description}</p>
            </TabPanel>
          ))}
          <div className='tw-mt-5 tw-flex tw-justify-center'>
            <TabList className='tw-inline-flex tw-gap-3' items={tabItems} aria-label='Technology layers'>
              {item => (
                <Tab
                  key={item.id}
                  id={item.id}
                  className={({ isSelected }) =>
                    clsx(
                      "focus-visible:tw-outline-focus tw-type-sm tw-flex tw-size-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border tw-outline-none tw-transition-colors",
                      isSelected
                        ? "tw-border-black tw-bg-black tw-text-white"
                        : "tw-border-stone-300 tw-bg-transparent tw-text-stone-500 hover:tw-border-black hover:tw-bg-black hover:tw-text-white",
                    )
                  }
                >
                  {item.title}
                </Tab>
              )}
            </TabList>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
